/**
 * Cola entre a UI web e o motor do app: abrir/entrar/sair, e o modelo que as telas leem.
 *
 * O modelo das telas (viewer) é construído do MESMO payload que o backup exporta
 * (`createBackupContent` sem cifra), então toda regra de leitura do viewer continua valendo e
 * toda escrita passa pelos repositórios do app. Depois de qualquer escrita ou sync, `loadModel`.
 */
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { BACKUP_APP_NAME, applyBackupContent, createBackupContent } from '@/lib/backupService';
import { isEncryptedBackup } from '@/lib/backupCrypto';
import { connectDrive, disconnectDrive, getLatestDriveBackup } from '@/lib/googleDrive/driveBackup';
import { downloadBackup } from '@/lib/googleDrive/driveClient';
import { getAccessToken } from '@/lib/googleDrive/googleAuth';
import {
  configureSyncKey,
  hasLocalUserData,
  joinExistingSyncSpace,
  remoteSpaceHasSegments,
  runSyncNow,
  setSyncProgressListener,
  type SyncProgress,
} from '@/lib/sync/sync';
import { useSyncStore } from '@/stores/useSyncStore';
import { useDriveBackupStore } from '@/stores/useDriveBackupStore';
import { closeDatabase, execute, flushSave, openDatabase } from '@/db/client';
import { hasPendingSyncWork } from '@/lib/sync/persistence';
import { setBackupPassphrase } from '@/lib/backupPassphrase';
import { GoogleSignin, withUserGesture } from './shims/google-signin';
import { clearSaved, loadSaved } from './db/persist';
import { buildModel, type Model } from './viewer/lib/model';
import type { BackupPayload } from './viewer/lib/types';

export { isEncryptedBackup };

/** Abre o banco: a cópia persistida deste navegador, ou um vazio com o boot real. Uma vez só por
 *  página — o StrictMode monta/desmonta/monta no dev e dois boots em paralelo migravam o mesmo banco. */
let booting: Promise<{ hasData: boolean }> | null = null;
export function boot(): Promise<{ hasData: boolean }> {
  return (booting ??= (async () => {
    const saved = await loadSaved();
    await openDatabase(saved ?? undefined);
    return { hasData: await hasLocalUserData() };
  })());
}

/** Payload do backup (sem cifra) → modelo das telas. */
export async function loadModel(): Promise<Model> {
  const { content } = await createBackupContent();
  return buildModel(JSON.parse(content) as BackupPayload);
}

export class NeedPassphraseError extends Error {
  constructor() { super('need-passphrase'); this.name = 'NeedPassphraseError'; }
}

/** Restaura um `.kontto`/`.json` no banco do navegador (substitui o que havia). */
export async function restoreText(text: string, passphrase: string | null): Promise<void> {
  if (isEncryptedBackup(text) && !passphrase) throw new NeedPassphraseError();
  const r = await applyBackupContent(text, { resolvePassphrase: async () => passphrase });
  if (!r.success) throw new Error(r.error ?? 'Não foi possível abrir esse arquivo.');
  await flushSave();
}

export async function restoreDemo(payload: BackupPayload): Promise<void> {
  // O restore do app só aceita o nome que ele mesmo grava no envelope; o demo do viewer diz 'Kontto'.
  await restoreText(JSON.stringify({ ...payload, app: BACKUP_APP_NAME }), null);
}

export type JoinStep = 'google' | 'checking' | 'key' | 'joining';
/** Andamento do passo "Seus dados" (vem do motor de sync; a tela traduz sem jargão). */
export type JoinProgress = SyncProgress;

/** O backup do Drive que a porta oferece a quem não tem a sincronização ligada. */
export interface DriveBackupRef {
  id: string;
  name: string;
  /** quando foi feito, na hora do celular que gerou ("2026-10-13T09:12:59"); null se o nome fugir do padrão */
  stamp: string | null;
}
/** O que a conta Google tem do Kontto: o espaço de sync de outro aparelho, ou só o backup. */
export type GoogleEntry =
  | { kind: 'sync'; email: string }
  | { kind: 'backup'; email: string; backup: DriveBackupRef };

/** "kontto_backup_2026-10-13_091259.kontto" → "2026-10-13T09:12:59". */
export function backupStamp(name: string): string | null {
  const m = /^kontto_backup_(\d{4}-\d{2}-\d{2})_(\d{2})(\d{2})(\d{2})/.exec(name);
  return m ? `${m[1]}T${m[2]}:${m[3]}:${m[4]}` : null;
}

/** Quando o backup foi feito, por extenso na porta ("13 de outubro às 09:12") ou curto no rótulo e na faixa ("13/10 às 09:12"). */
export function backupWhen(stamp: string | null, short = false): string | null {
  if (!stamp) return null;
  const d = parseISO(stamp);
  if (Number.isNaN(d.getTime())) return null;
  return short ? format(d, "dd/MM 'às' HH:mm") : format(d, "d 'de' MMMM 'às' HH:mm", { locale: ptBR });
}

/**
 * A base aberta veio do backup do Drive — e de quando. Precisa sobreviver ao F5: quem volta dias depois
 * continua vendo AQUELA cópia, não o celular de hoje, e o rótulo e a faixa da tela dizem de quando ela é.
 */
const DRIVE_BACKUP_KEY = 'kontto-web:drive-backup';
export function savedDriveBackup(): { stamp: string | null } | null {
  try {
    const raw = localStorage.getItem(DRIVE_BACKUP_KEY);
    return raw == null ? null : { stamp: raw || null };
  } catch { return null; }
}
/** `false` = a base aberta não é (mais) um backup do Drive. */
export function rememberDriveBackup(stamp: string | null | false): void {
  try { if (stamp === false) localStorage.removeItem(DRIVE_BACKUP_KEY); else localStorage.setItem(DRIVE_BACKUP_KEY, stamp ?? ''); } catch {}
}

/**
 * Entrar pelo Google, em dois passos — a conta ANTES da senha:
 *  1. `connectGoogle`: popup da conta e a conferência do que há na pasta do Kontto. Com segmentos de
 *     OUTRO aparelho, é o espaço de sync (`joinSpace`). Sem eles mas com backup, oferece abrir o
 *     backup mais recente (`openDriveBackup`): quem só liga o backup no celular entrava no
 *     visualizador antigo e aqui batia num erro. Sem nada, NÃO cria um espaço (a Fase 1 só segue um
 *     celular) e desfaz a conexão, pra pessoa descobrir isso antes de digitar senha alguma.
 *  2. `joinSpace`: senha E2E → chave compartilhada (conferida contra o `kontto_sync_meta.json`)
 *     → reconstrói a base daqui pelo oplog. A chave fica neste navegador: a senha é pedida uma vez.
 */
export async function connectGoogle(onStep?: (s: JoinStep) => void): Promise<GoogleEntry> {
  onStep?.('google');
  const c = await connectDrive();
  if (!c.ok) throw new Error(c.error ?? 'Não foi possível conectar ao Google.');
  onStep?.('checking');
  let found = false;
  let latest: Awaited<ReturnType<typeof getLatestDriveBackup>> = null;
  try {
    found = await remoteSpaceHasSegments();
    if (!found) latest = await getLatestDriveBackup();
  } catch (e) { await disconnectGoogle(); throw e; }
  const email = c.email ?? useDriveBackupStore.getState().connectedEmail ?? '';
  if (found) return { kind: 'sync', email };
  if (latest) return { kind: 'backup', email, backup: { id: latest.id, name: latest.name, stamp: backupStamp(latest.name) } };
  await disconnectGoogle();
  throw new Error('Não achei dados do Kontto nesta conta do Google. No celular, em Configurações › Backup, conecte esta conta e faça um backup, ou ligue "Sincronizar aparelhos", e tente de novo.');
}

/** O arquivo já baixado, pra senha errada não custar outro download. */
let fetchedBackup: { id: string; text: string } | null = null;

/**
 * Abre o backup do Drive de quem não tem a sincronização: baixa, decifra com a Senha de proteção e
 * monta a base daqui. Vale o mesmo do "Abrir um arquivo": o que for editado fica só neste navegador.
 * Por isso a conexão com o Google é desfeita no fim — sem sync ela não serve pra nada, e ao recarregar
 * a tela diria "Sync · e-mail" sobre uma base que não sincroniza. Senha errada mantém a conexão.
 */
export async function openDriveBackup(backup: DriveBackupRef, passphrase: string): Promise<void> {
  if (fetchedBackup?.id !== backup.id) {
    const token = await getAccessToken();
    fetchedBackup = { id: backup.id, text: await downloadBackup(token, backup.id) };
  }
  await restoreText(fetchedBackup.text, passphrase);
  await disconnectGoogle();
}

export async function joinSpace(passphrase: string, onStep?: (s: JoinStep, progress?: JoinProgress) => void): Promise<void> {
  onStep?.('key');
  await configureSyncKey(passphrase);
  // A mesma senha vale pro backup: o motor do app (`maybeSyncNow`, backups) confere `hasBackupPassphrase`
  // antes de rodar; sem isto o ciclo pós-escrita parava em "configurar de novo" sem ninguém ver.
  await setBackupPassphrase(passphrase);
  onStep?.('joining');
  setSyncProgressListener((p) => onStep?.('joining', p));
  try {
    const r = await joinExistingSyncSpace();
    if (!r.ok) throw new Error(r.error ?? 'Não foi possível sincronizar.');
  } finally {
    setSyncProgressListener(null);
  }
  useSyncStore.getState().markSynced();
  await flushSave();
}

/** Desfaz só a conexão com o Google (quem desiste antes da senha). */
export async function disconnectGoogle(): Promise<void> {
  fetchedBackup = null;
  try { await disconnectDrive(); } catch { /* já estava fora */ }
}

export function syncEnabled(): boolean {
  return useSyncStore.getState().enabled;
}

export function connectedEmail(): string | null {
  return useDriveBackupStore.getState().connectedEmail;
}

/** Um ciclo de sync (sobe o que mudou aqui, baixa o que mudou lá). */
export async function syncOnce(): Promise<{ ok: boolean; error?: string }> {
  const r = await runSyncNow();
  await flushSave();
  return { ok: r.ok, error: r.error };
}

/**
 * Clique em "Reconectar"/"Sincronizar": pede o token do Google AINDA dentro do gesto do usuário — o GIS
 * abre um popup, e fora de um clique o navegador bloqueia (ver shims/google-signin). Com token válido
 * não abre nada. Só depois disso o ciclo normal sobe o que ficou esperando.
 */
export async function reconnectGoogle(): Promise<void> {
  await withUserGesture(() => GoogleSignin.getTokens());
}

/** Linhas escritas neste navegador que ainda não subiram pro Drive (outbox + oplog marcado pending_upload). */
export async function pendingUploads(): Promise<number> {
  if (!syncEnabled()) return 0;
  try {
    const r = await execute('SELECT (SELECT COUNT(DISTINCT row_id) FROM sync_outbox) + (SELECT COUNT(DISTINCT row_id) FROM sync_messages WHERE pending_upload = 1) AS n');
    return Number(r.rows[0]?.n ?? 0);
  } catch { return 0; }
}

/** Espera depois da ÚLTIMA escrita antes de subir (salvar costuma vir em rajada), como no app. */
const AFTER_WRITE_DELAY_MS = 3_000;
let afterWriteTimer: number | null = null;

/**
 * Depois de uma escrita local: grava a cópia do navegador já e agenda o envio. Vai pelo `runSyncNow`
 * (o mesmo do botão), não pelo `scheduleSyncAfterWrite` do app: aquele passa por `maybeSyncNow`, que
 * exige a senha de backup guardada — sessões antigas da web só têm a chave de sync, e o envio
 * pós-escrita falhava em silêncio ("configurar de novo") enquanto o botão dizia "sincronizado".
 */
export async function afterLocalWrite(): Promise<void> {
  await flushSave();
  if (!syncEnabled()) return;
  if (afterWriteTimer) window.clearTimeout(afterWriteTimer);
  afterWriteTimer = window.setTimeout(() => { afterWriteTimer = null; void sendPendingNow(); }, AFTER_WRITE_DELAY_MS);
}

/** Sobe o que está esperando, se houver. O resultado (ou o erro) vai pro store de sync e a tela acompanha por lá. */
export async function sendPendingNow(): Promise<void> {
  if (!syncEnabled()) return;
  try {
    if (!(await hasPendingSyncWork())) return;
    await syncOnce();
  } catch (e) {
    console.warn('[kontto-web] envio pós-escrita falhou:', e);
  }
}

/** Sai: desconecta o Google, descarta o banco deste navegador e recarrega na porta. */
export async function leave(): Promise<void> {
  try { await disconnectDrive(); } catch {}
  closeDatabase();
  await clearSaved();
  try {
    for (const k of Object.keys(localStorage)) if (k.startsWith('kontto-web:')) localStorage.removeItem(k);
    sessionStorage.clear();
  } catch {}
  location.reload();
}
